import { useEffect, useState } from 'react';
import { Grid, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, TextField, Typography, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TimePickerField from '../common/TimePickerField';
import WeekdaySelect from '../common/WeekdaySelect';
import ListTransfer from '../common/ListTransfer';
import { gettingOvertime } from 'utils/Zones';
import { useZone } from '../ZoneContext';
import { useZoneActions } from '../ZoneContext';

const ConsecutiveDaysWorked = ({open}) => {
    const { t } = useTranslation();
    const zone = useZone();
    const { updateField } = useZoneActions();
    const consecutiveData = zone.consecutiveDays;
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            try {
                setLoading(true);
                gettingOvertime(
                    (loading) => setLoading(loading),
                    (overtimes) => updateField('consecutiveDays', 'overtimeSustitutions', overtimes)
                );
            } catch (error) {
                console.error('Error fetching overtime sustitutitions:', error);
            } finally {
                setLoading(false);
            }
        }
    }, [open, updateField]);


    return (
        <>
            {loading ?
            <Grid container justifyContent="center" sx={{ py: 4 }}>
                <CircularProgress />
            </Grid> :
                <Grid container spacing={2} sx={{ mt: 2 }}>
                    {/* Day Amount, Min Hours, Min Hours Last Day */}
                    <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }} >
                        <TextField
                            id="day_amount"
                            label={t('settings.zones.fields.consecutive_days_worked.day_amount')}
                            variant="outlined"
                            type="number"
                            value={consecutiveData.dayAmount ? consecutiveData.dayAmount : 0}
                            onChange={(e) => updateField('consecutiveDays', 'dayAmount', parseInt(e.target.value) || 0)}
                            fullWidth
                            slotProps={{
                                htmlInput: {
                                    min: 0,
                                    max: 100
                                }
                            }}
                            required
                        />
                    </Grid>

                    <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
                        <TimePickerField
                            id="min_hours"
                            label={t('settings.zones.fields.consecutive_days_worked.min_hours')}
                            value={consecutiveData.minHours}
                            onChange={(value) => updateField('consecutiveDays', 'minHours', value)}
                        />
                    </Grid>

                    <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
                        <TimePickerField
                            id="min_hours_last_day"
                            label={t('settings.zones.fields.consecutive_days_worked.min_hours_of_the_last_day')}
                            value={consecutiveData.minHoursLastDay}
                            onChange={(value) => updateField('consecutiveDays', 'minHoursLastDay', value)}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <FormControl fullWidth>
                            <FormLabel id="consecutive-type-label">{t('settings.zones.fields.consecutive_days_worked.consecutive_type')}</FormLabel>

                            <RadioGroup
                                aria-labelledby="consecutive-type-label"
                                value={consecutiveData.typeConsecutive}
                                defaultValue={'consecute_days'}
                                onChange={(e) => updateField('consecutiveDays', 'typeConsecutive', e.target.value)}
                            >
                                <FormControlLabel
                                    value='consecute_days'
                                    control={<Radio />}
                                    label={t('settings.zones.fields.consecutive_days_worked.consecutive_days')}
                                />

                                {/* Reset To Options - Solo visible si typeConsecutive es 'consecute_days' */}
                                {consecutiveData.typeConsecutive === 'consecute_days' && (
                                    <Grid container sx={{ paddingLeft: 3.5 }}>
                                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                                            <FormControl fullWidth >
                                                <FormLabel id="reset-to-label">
                                                    {t('settings.zones.fields.consecutive_days_worked.reset_to')}
                                                </FormLabel>
                                                <RadioGroup
                                                    aria-labelledby="reset-to-label"
                                                    defaultValue={'day_number'}
                                                    value={consecutiveData.resetTo}
                                                    onChange={(e) => updateField('consecutiveDays', 'resetTo', e.target.value)}
                                                >
                                                    <Grid container sx={{ mt: 2 , paddingLeft: 3.5 }}>
                                                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                                            <FormControlLabel
                                                                value="day_number"
                                                                control={<Radio />}
                                                                label={t('settings.zones.fields.consecutive_days_worked.day_number')}
                                                            />
                                                        </Grid>
                                                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                label={t('settings.zones.fields.consecutive_days_worked.day_number')}
                                                                value={consecutiveData.dayNumber ? consecutiveData.dayNumber : 0}
                                                                onChange={(e) => updateField('consecutiveDays', 'dayNumber', parseInt(e.target.value) || 0)}
                                                                disabled={consecutiveData.resetTo !== 'day_number'}
                                                                fullWidth
                                                                slotProps={{
                                                                    htmlInput: {
                                                                        min: 0,
                                                                        max: 100
                                                                    }
                                                                }}
                                                            />
                                                        </Grid>
                                                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }} sx={{ mt: 2 }}>
                                                            <FormControlLabel
                                                                value="days_week"
                                                                control={<Radio />}
                                                                label={t('settings.zones.fields.consecutive_days_worked.days_week')}
                                                            />
                                                        </Grid>
                                                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }} sx={{ mt: 2 }}>
                                                            <WeekdaySelect
                                                                value={consecutiveData.weekday1}
                                                                onChange={(e) => updateField('consecutiveDays', 'weekday1', e.target.value)}
                                                                label={t('settings.zones.fields.consecutive_days_worked.days_week')}
                                                                disabled={consecutiveData.resetTo !== 'days_week'}
                                                                size="small"
                                                                fullWidth
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </RadioGroup>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                )}
                                <Grid container sx={{ mt: 2 }}>
                                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                                        <FormControlLabel
                                            value={'days_week'}
                                            control={<Radio/>}
                                            label={t('settings.zones.fields.consecutive_days_worked.days_week')}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container sx={{ paddingLeft: 3.5 }}>
                                    <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                        <FormLabel id="reset-to-label">
                                            {t('settings.zones.fields.consecutive_days_worked.reset_to')}
                                        </FormLabel>
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }} sx={{ paddingLeft: 1.5 }}>
                                        <WeekdaySelect
                                            value={consecutiveData.weekday2}
                                            onChange={(e) => updateField('consecutiveDays', 'weekday2', e.target.value)}
                                            label={t('settings.zones.fields.consecutive_days_worked.days_week')}
                                            disabled={consecutiveData.typeConsecutive !== 'days_week'}
                                            size="small"
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                    {/* Overtime Substitution */}
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <Typography variant="h6" gutterBottom>
                            {t('settings.zones.fields.consecutive_days_worked.overtime_sustitution')}
                        </Typography>
                        <ListTransfer
                            loading={loading}
                            allItems={consecutiveData.overtimeSustitutions}
                            selectedItems={consecutiveData.selectedOvertimeSustitution}
                            onSelectionChange={(selected) => updateField('consecutiveDays', 'selectedOvertimeSustitution', selected)}
                            textLeft={t('settings.zones.fields.holiday.holidays_available')}
                            textRight={t('settings.zones.fields.holiday.selected_holidays')}
                            textSelected={t('settings.zones.fields.holiday.selected')}
                        />
                    </Grid>
                </Grid>
            }
        </>

    );
}

export default ConsecutiveDaysWorked